import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
  getCountFromServer
} from 'firebase/firestore';
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  PhoneAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  getAuth,
  ConfirmationResult,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updatePassword,
  updateProfile,
  ActionCodeSettings
} from 'firebase/auth';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import firebaseApp, { db, auth, storage } from '../config/firebase';
import { User, Tour, Booking, Blog, Settings, Notification } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { getTranslatedData } from '../utils/getTranslatedData';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Auth Services
export const authService = {
  auth: getAuth(firebaseApp),
  
  createRecaptchaVerifier: (container: HTMLElement) => {
    return new RecaptchaVerifier(authService.auth, container, {
      'size': 'invisible',
      'callback': (response: any) => {
        console.log('reCAPTCHA solved, user can sign in.');
      },
      'expired-callback': () => {
        console.log('reCAPTCHA expired, need to solve again.');
      }
    });
  },

  // Phone authentication
  signInWithPhone: async (phoneNumber: string, appVerifier: RecaptchaVerifier): Promise<ConfirmationResult> => {
    try {
      const confirmationResult = await signInWithPhoneNumber(authService.auth, phoneNumber, appVerifier);
      return confirmationResult;
    } catch (error) {
      throw error;
    }
  },

  // Verify phone code
  verifyPhoneCode: async (confirmationResult: any, code: string) => {
    try {
      const result = await confirmationResult.confirm(code);
      return result.user;
    } catch (error) {
      throw error;
    }
  },

  // Sign out
  signOut: async () => {
    try {
      await signOut(authService.auth);
    } catch (error) {
      throw error;
    }
  },

  // Auth state listener
  onAuthStateChanged: (callback: (user: FirebaseUser | null) => void) => {
    return onAuthStateChanged(authService.auth, callback);
  },

  registerWithEmail: async (email: string, password: string) => {
    return await createUserWithEmailAndPassword(authService.auth, email, password);
  },

  loginWithEmail: async (email: string, password: string) => {
    return await signInWithEmailAndPassword(authService.auth, email, password);
  },

  // Email link authentication
  sendSignInLinkToEmail: async (email: string, actionCodeSettings: ActionCodeSettings) => {
    return await sendSignInLinkToEmail(authService.auth, email, actionCodeSettings);
  },

  isSignInWithEmailLink: (url: string) => {
    return isSignInWithEmailLink(authService.auth, url);
  },

  signInWithEmailLink: async (email: string, url: string) => {
    return await signInWithEmailLink(authService.auth, email, url);
  },

  updatePassword: async (user: FirebaseUser, newPassword: string):Promise<void> => {
    return await updatePassword(user, newPassword);
  },

  updateUserProfile: async (user: FirebaseUser, profile: { displayName?: string, photoURL?: string }) => {
    return await updateProfile(user, profile);
  }
};

// Global Services (like calling a cloud function)
const functions = getFunctions(firebaseApp);

export const globalService = {
  translateTexts: async (data: { text: string; sourceLang: string; targetLangs: string[] }): Promise<{ [key: string]: string }> => {
    try {
      const translateTextCallable = httpsCallable(functions, 'translateText');
      const result = await translateTextCallable(data);
      return result.data as { [key: string]: string };
    } catch (error) {
      console.error("Error calling translateText function:", error);
      throw new Error("Translation failed. Please check the function logs.");
    }
  }
};

// User Services
export const userService = {
  // Get user by ID
  getUser: async (uid: string): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return { uid: userDoc.id, ...userDoc.data() } as User;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  getUsersCount: async (): Promise<number> => {
    try {
      const snapshot = await getCountFromServer(collection(db, 'users'));
      return snapshot.data().count;
    } catch (error) {
      console.error("Error getting users count: ", error);
      return 0;
    }
  },

  getAllUsers: async (): Promise<User[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      return querySnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }) as User);
    } catch (error) {
      console.error("Error getting all users: ", error);
      return [];
    }
  },

  // Create user
  createUser: async (userData: Omit<User, 'uid' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const userRef = await addDoc(collection(db, 'users'), {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return userRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Update user
  updateUser: async (uid: string, userData: Partial<User>): Promise<void> => {
    try {
      await updateDoc(doc(db, 'users', uid), {
        ...userData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Create user with specific UID
  createUserWithUID: async (uid: string, userData: Omit<User, 'uid' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
      console.log('Creating user with UID:', uid, 'and data:', userData);
      await setDoc(doc(db, 'users', uid), {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log('User created with UID:', uid, 'in Firestore.');
    } catch (error) {
      console.error('Error creating user with UID:', error);
      throw error;
    }
  }
};

// Tour Services
export const tourService = {
  getToursCount: async (): Promise<number> => {
    try {
      const snapshot = await getCountFromServer(collection(db, 'tours'));
      return snapshot.data().count;
    } catch (error) {
      console.error("Error getting tours count: ", error);
      return 0;
    }
  },

  // Get all tours
  getTours: async (filters?: any): Promise<Tour[]> => {
    try {
      let q = query(collection(db, 'tours'));
      
      // Eğer isActive filtresi varsa uygula
      if (filters?.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }
      
      if (filters?.category) {
        q = query(q, where('category', '==', filters.category));
      }
      
      if (filters?.isFeatured) {
        q = query(q, where('isFeatured', '==', true));
      }
      
      // Sıralama - createdAt varsa onu kullan, yoksa name ile sırala
      try {
        q = query(q, orderBy('createdAt', 'desc'));
      } catch {
        q = query(q, orderBy('name', 'asc'));
      }
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Tour);
    } catch (error) {
      console.error('Error fetching tours:', error);
      // Hata durumunda basit sorgu dene
      try {
        const querySnapshot = await getDocs(collection(db, 'tours'));
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Tour);
      } catch (fallbackError) {
        throw fallbackError;
      }
    }
  },

  // Get tour by ID
  getTour: async (id: string): Promise<Tour | null> => {
    try {
      const tourDoc = await getDoc(doc(db, 'tours', id));
      if (tourDoc.exists()) {
        return { id: tourDoc.id, ...tourDoc.data() } as Tour;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Get featured tours
  getFeaturedTours: async (limitCount: number = 6): Promise<Tour[]> => {
    try {
      let q = query(collection(db, 'tours'));
      
      // isFeatured filtresi varsa uygula
      try {
        q = query(q, where('isFeatured', '==', true));
      } catch {
        // isFeatured alanı yoksa tüm turları al
        console.log('isFeatured field not found, getting all tours');
      }
      
      // Sıralama
      try {
        q = query(q, orderBy('createdAt', 'desc'));
      } catch {
        q = query(q, orderBy('name', 'asc'));
      }
      
      q = query(q, limit(limitCount));
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Tour);
    } catch (error) {
      console.error('Error fetching featured tours:', error);
      // Hata durumunda basit sorgu dene
      try {
        const querySnapshot = await getDocs(collection(db, 'tours'));
        return querySnapshot.docs.slice(0, limitCount).map(doc => ({ id: doc.id, ...doc.data() }) as Tour);
      } catch (fallbackError) {
        throw fallbackError;
      }
    }
  },

  // Delete a tour
  deleteTour: async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'tours', id));
    } catch (error) {
      console.error("Error deleting tour: ", error);
      throw error;
    }
  },

  // Update an existing tour
  updateTour: async (id: string, tourData: Partial<Omit<Tour, 'id' | 'createdAt'>>): Promise<void> => {
    try {
      await updateDoc(doc(db, 'tours', id), {
        ...tourData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Error updating tour: ", error);
      throw error;
    }
  },

  // Create a new tour
  createTour: async (tourData: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const tourRef = await addDoc(collection(db, 'tours'), {
        ...tourData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return tourRef.id;
    } catch (error) {
      throw error;
    }
  }
};

// Booking Services
export const bookingService = {
  getBookingsCount: async (): Promise<number> => {
    try {
      const snapshot = await getCountFromServer(collection(db, 'bookings'));
      return snapshot.data().count;
    } catch (error) {
      console.error("Error getting bookings count: ", error);
      return 0;
    }
  },

  getAllBookings: async (): Promise<Booking[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, 'bookings'));
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          isGuestBooking: data.isGuestBooking || false
        } as Booking;
      });
    } catch (error) {
      console.error("Error getting all bookings: ", error);
      return [];
    }
  },

  // Create booking
  createBooking: async (bookingData: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const bookingRef = await addDoc(collection(db, 'bookings'), {
        ...bookingData,
        isGuestBooking: !bookingData.userId, // userId yoksa misafir rezervasyonu
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return bookingRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get user bookings
  getUserBookings: async (userId: string): Promise<Booking[]> => {
    try {
      console.log('getUserBookings called with userId:', userId);
      
      if (!userId) {
        console.log('No userId provided, returning empty array');
        return [];
      }
      
      // Önce tüm rezervasyonları kontrol edelim
      try {
        const allBookingsQuery = query(collection(db, 'bookings'));
        const allBookingsSnapshot = await getDocs(allBookingsQuery);
        console.log('Total bookings in database:', allBookingsSnapshot.docs.length);
        console.log('All bookings:', allBookingsSnapshot.docs.map(doc => ({ id: doc.id, userId: doc.data().userId })));
      } catch (allBookingsError) {
        console.log('Could not fetch all bookings:', allBookingsError);
      }
      
      // Basit sorgu yap, sıralama olmadan
      let q = query(
        collection(db, 'bookings'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      console.log('Query result count for userId', userId, ':', querySnapshot.docs.length);
      
      const bookings = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Booking data for doc', doc.id, ':', data);
        
        // Timestamp'leri kontrol et
        const booking: Booking = {
          id: doc.id,
          userId: data.userId,
          tourId: data.tourId,
          tourName: data.tourName,
          customerName: data.customerName,
          customerEmail: data.customerEmail,
          customerPhone: data.customerPhone,
          bookingDate: data.bookingDate || data.createdAt,
          tourDate: data.tourDate,
          participants: data.participants || data.passengerCount || 0,
          totalPrice: data.totalPrice,
          currency: data.currency || 'TRY',
          status: data.status,
          paymentStatus: data.paymentStatus,
          specialRequests: data.specialRequests,
          tourDetails: data.tourDetails || { destination: getTranslatedData(data.tourName), duration: 'N/A' },
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          paymentDate: data.paymentDate,
          adminNotes: data.adminNotes,
          isGuestBooking: data.isGuestBooking || false
        };
        
        console.log('Processed booking:', booking);
        return booking;
      });
      
      console.log('Final processed bookings:', bookings);
      return bookings;
    } catch (error) {
      console.error('Error in getUserBookings:', error);
      
      // Hata detaylarını logla
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      
      // Hata durumunda boş array döndür
      return [];
    }
  },

  // Get booking by ID
  getBooking: async (id: string): Promise<Booking | null> => {
    try {
      const bookingDoc = await getDoc(doc(db, 'bookings', id));
      if (bookingDoc.exists()) {
        const data = bookingDoc.data();
        return { 
          id: bookingDoc.id, 
          ...data,
          isGuestBooking: data.isGuestBooking || false
        } as Booking;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update booking status
  updateBookingStatus: async (id: string, status: Booking['status'], paymentStatus?: Booking['paymentStatus']): Promise<void> => {
    try {
      const updateData: any = { status, updatedAt: serverTimestamp() };
      if (paymentStatus) {
        updateData.paymentStatus = paymentStatus;
        updateData.paymentDate = serverTimestamp();
      }
      
      await updateDoc(doc(db, 'bookings', id), updateData);
    } catch (error) {
      throw error;
    }
  }
};

// Blog Services
export const blogService = {
  getBlogsCount: async (): Promise<number> => {
    try {
      const snapshot = await getCountFromServer(collection(db, 'blogs'));
      return snapshot.data().count;
    } catch (error) {
      console.error("Error getting blogs count: ", error);
      return 0;
    }
  },

  // Get all blogs
  getBlogs: async (filters?: any): Promise<Blog[]> => {
    try {
      // Önce basit sorgu yap, sıralama olmadan
      let q = query(collection(db, 'blogs'));
      
      // isPublished filtresi varsa uygula - farklı alan isimlerini kontrol et
      if (filters?.isPublished !== undefined) {
        try {
          q = query(q, where('isPublished', '==', filters.isPublished));
        } catch {
          try {
            q = query(q, where('published', '==', filters.isPublished));
          } catch {
            try {
              q = query(q, where('status', '==', filters.isPublished ? 'published' : 'draft'));
            } catch {
              // No published field found, getting all blogs
            }
          }
        }
      }
      
      if (filters?.category) {
        q = query(q, where('category', '==', filters.category));
      }
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.docs.length === 0) {
        return [];
      }
      
      const blogs = querySnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Veri yapısını normalize et - daha kapsamlı kontrol
        const normalizedBlog = {
          id: doc.id,
          title: data.title || data.name || data.headline || data.heading || '',
          content: data.content || data.body || data.text || data.description || '',
          excerpt: data.excerpt || data.summary || data.description || data.content?.substring(0, 150) || '',
          author: data.author || data.writer || data.createdBy || data.user || 'Anonim',
          category: data.category || data.type || data.tags?.[0] || data.categories?.[0] || '',
          tags: data.tags || data.keywords || data.categories || [],
          featuredImage: data.featuredImage || data.image || data.coverImage || data.thumbnail || data.photo || '',
          isPublished: data.isPublished !== undefined ? data.isPublished : 
                      data.published !== undefined ? data.published :
                      data.status === 'published' ? true :
                      data.status === 'draft' ? false :
                      data.status === 'active' ? true :
                      data.status === 'inactive' ? false :
                      true, // Varsayılan olarak true
          status: data.status || (data.isPublished ? 'published' : 'draft'),
          seoTitle: data.seoTitle || data.title || data.name || '',
          seoDescription: data.seoDescription || data.excerpt || data.description || data.summary || '',
          seoKeywords: data.seoKeywords || data.tags || data.keywords || [],
          viewCount: data.viewCount || data.views || data.readCount || data.visits || 0,
          createdAt: data.createdAt || data.date || data.timestamp || data.created || new Date(),
          publishedAt: data.publishedAt || data.publishedDate || data.createdAt || data.date || new Date(),
          updatedAt: data.updatedAt || data.modified || data.updated || data.createdAt || new Date()
        };
        
        return normalizedBlog as Blog;
      });
      
      // JavaScript tarafında sıralama yap
      blogs.sort((a, b) => {
        // Önce publishedAt'e göre sırala
        if (a.publishedAt && b.publishedAt) {
          const dateA = a.publishedAt instanceof Date ? a.publishedAt : a.publishedAt.toDate();
          const dateB = b.publishedAt instanceof Date ? b.publishedAt : b.publishedAt.toDate();
          return dateB.getTime() - dateA.getTime();
        }
        // Sonra createdAt'e göre sırala
        if (a.createdAt && b.createdAt) {
          const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt.toDate();
          const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt.toDate();
          return dateB.getTime() - dateA.getTime();
        }
        // Son olarak başlığa göre sırala
        const titleA = getTranslatedData(a.title, 'tr');
        const titleB = getTranslatedData(b.title, 'tr');
        return titleA.localeCompare(titleB);
      });
      
      return blogs;
    } catch (error) {
      console.error('Error fetching blogs:', error);
      // Hata durumunda basit sorgu dene
      try {
        const querySnapshot = await getDocs(collection(db, 'blogs'));
        
        const blogs = querySnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Veri yapısını normalize et
          const normalizedBlog = {
            id: doc.id,
            title: data.title || data.name || data.headline || data.heading || '',
            content: data.content || data.body || data.text || data.description || '',
            excerpt: data.excerpt || data.summary || data.description || data.content?.substring(0, 150) || '',
            author: data.author || data.writer || data.createdBy || data.user || 'Anonim',
            category: data.category || data.type || data.tags?.[0] || data.categories?.[0] || '',
            tags: data.tags || data.keywords || data.categories || [],
            featuredImage: data.featuredImage || data.image || data.coverImage || data.thumbnail || data.photo || '',
            isPublished: data.isPublished !== undefined ? data.isPublished : 
                        data.published !== undefined ? data.published :
                        data.status === 'published' ? true :
                        data.status === 'draft' ? false :
                        data.status === 'active' ? true :
                        data.status === 'inactive' ? false :
                        true, // Varsayılan olarak true
            status: data.status || (data.isPublished ? 'published' : 'draft'),
            seoTitle: data.seoTitle || data.title || data.name || '',
            seoDescription: data.seoDescription || data.excerpt || data.description || data.summary || '',
            seoKeywords: data.seoKeywords || data.tags || data.keywords || [],
            viewCount: data.viewCount || data.views || data.readCount || data.visits || 0,
            createdAt: data.createdAt || data.date || data.timestamp || data.created || new Date(),
            publishedAt: data.publishedAt || data.publishedDate || data.createdAt || data.date || new Date(),
            updatedAt: data.updatedAt || data.modified || data.updated || data.createdAt || new Date()
          };
          
          return normalizedBlog as Blog;
        });
        
        return blogs;
      } catch (fallbackError) {
        console.error('Fallback error:', fallbackError);
        throw fallbackError;
      }
    }
  },

  // Get blog by ID
  getBlog: async (id: string): Promise<Blog | null> => {
    try {
      const blogDoc = await getDoc(doc(db, 'blogs', id));
      if (blogDoc.exists()) {
        return { id: blogDoc.id, ...blogDoc.data() } as Blog;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Get featured blogs
  getFeaturedBlogs: async (limitCount: number = 3): Promise<Blog[]> => {
    try {
      let q = query(collection(db, 'blogs'));
      
      // Sıralama - viewCount varsa onu kullan
      try {
        q = query(q, orderBy('viewCount', 'desc'));
      } catch {
        try {
          q = query(q, orderBy('createdAt', 'desc'));
        } catch {
          q = query(q, orderBy('title', 'asc'));
        }
      }
      
      q = query(q, limit(limitCount));
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Blog);
    } catch (error) {
      console.error('Error fetching featured blogs:', error);
      // Hata durumunda basit sorgu dene
      try {
        const querySnapshot = await getDocs(collection(db, 'blogs'));
        return querySnapshot.docs.slice(0, limitCount).map(doc => ({ id: doc.id, ...doc.data() }) as Blog);
      } catch (fallbackError) {
        throw fallbackError;
      }
    }
  },

  // Increment view count
  incrementViewCount: async (id: string): Promise<void> => {
    try {
      const blogRef = doc(db, 'blogs', id);
      const blogDoc = await getDoc(blogRef);
      if (blogDoc.exists()) {
        const currentViews = blogDoc.data().viewCount || 0;
        await updateDoc(blogRef, { viewCount: currentViews + 1 });
      }
    } catch (error) {
      throw error;
    }
  },

  // Add a new blog
  addBlog: async (blogData: Omit<Blog, 'id' | 'createdAt' | 'updatedAt' | 'viewCount'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'blogs'), {
        ...blogData,
        viewCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: blogData.isPublished ? serverTimestamp() : null,
      });
      return docRef.id;
    } catch (error) {
      console.error("Error adding blog: ", error);
      throw error;
    }
  },

  // Update a blog
  updateBlog: async (id: string, blogData: Partial<Omit<Blog, 'id'>>): Promise<void> => {
    try {
      const blogRef = doc(db, 'blogs', id);
      const existingDoc = await getDoc(blogRef);

      if (!existingDoc.exists()) {
        throw new Error("Blog not found!");
      }

      const existingData = existingDoc.data() as Blog;

      const updateData: { [key: string]: any } = {
        ...blogData,
        updatedAt: serverTimestamp(),
      };
      
      // If the blog is being published for the first time
      if (blogData.isPublished && !existingData.isPublished) {
        updateData.publishedAt = serverTimestamp();
      }

      await updateDoc(blogRef, updateData);
    } catch (error) {
      console.error("Error updating blog: ", error);
      throw error;
    }
  },

  // Delete a blog
  deleteBlog: async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'blogs', id));
    } catch (error) {
      console.error("Error deleting blog: ", error);
      throw error;
    }
  }
};

// Settings Services
export const settingsService = {
  // Get settings
  getSettings: async (): Promise<Settings | null> => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'main'));
      if (settingsDoc.exists()) {
        return { id: settingsDoc.id, ...settingsDoc.data() } as Settings;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update settings (partial)
  updateSettings: async (data: Partial<Settings>): Promise<void> => {
    try {
      const docRef = doc(db, 'settings', 'main');
      const existingDoc = await getDoc(docRef);
      if (existingDoc.exists()) {
        await updateDoc(docRef, {
          ...data,
          updatedAt: serverTimestamp()
        });
      } else {
        await setDoc(docRef, {
          ...data,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      throw error;
    }
  }
};

// Storage Services
export const storageService = {
  // Upload a single file with progress tracking
  uploadFile: async (file: File, path: string, onProgress?: (progress: number) => void): Promise<string> => {
    try {
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return new Promise<string>((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            // Progress tracking
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) {
              onProgress(progress);
            }
            console.log(`Upload ${progress.toFixed(1)}% done for ${file.name}`);
          },
          (error) => {
            // Error handling
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            // Upload completed successfully
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      throw error;
    }
  },

  // Upload a single file (legacy method without progress)
  uploadFileSimple: async (file: File, path: string): Promise<string> => {
    try {
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      throw error;
    }
  },

  // Upload multiple files and return their URLs with progress tracking
  uploadMultipleFiles: async (
    files: File[], 
    basePath: string, 
    onProgress?: (fileIndex: number, progress: number, totalProgress: number) => void
  ): Promise<string[]> => {
    const uploadPromises = files.map((file, index) => {
      const filePath = `${basePath}/${uuidv4()}-${file.name}`;
      return storageService.uploadFile(file, filePath, (progress) => {
        if (onProgress) {
          const totalProgress = ((index + progress / 100) / files.length) * 100;
          onProgress(index, progress, totalProgress);
        }
      });
    });

    try {
      const urls = await Promise.all(uploadPromises);
      return urls;
    } catch (error) {
      console.error("Error uploading multiple files:", error);
      throw error;
    }
  },

  // Delete file
  deleteFile: async (path: string): Promise<void> => {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (error) {
      throw error;
    }
  }
};

// Notification Services
export const notificationService = {
  // Create notification
  createNotification: async (notificationData: Omit<Notification, 'id' | 'createdAt' | 'sentAt'>): Promise<string> => {
    try {
      const notificationRef = await addDoc(collection(db, 'notifications'), {
        ...notificationData,
        createdAt: serverTimestamp(),
        sentAt: serverTimestamp()
      });
      return notificationRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get user notifications
  getUserNotifications: async (userId: string): Promise<Notification[]> => {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Notification);
    } catch (error) {
      throw error;
    }
  },

  // Mark notification as read
  markAsRead: async (id: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'notifications', id), { isRead: true });
    } catch (error) {
      throw error;
    }
  }
};

// About Page Services
export const aboutPageService = {
  // Get about page data
  getAboutPageData: async (): Promise<any> => {
    try {
      const aboutDoc = await getDoc(doc(db, 'aboutPage', 'main'));
      if (aboutDoc.exists()) {
        return { id: aboutDoc.id, ...aboutDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update about page data
  updateAboutPageData: async (aboutData: any): Promise<void> => {
    try {
      const docRef = doc(db, 'aboutPage', 'main');
      await setDoc(docRef, {
        ...aboutData,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error: any) {
      throw new Error('Hakkımızda sayfası güncellenirken hata oluştu: ' + error.message);
    }
  },

  // Initialize default about page data
  initializeAboutPageData: async (): Promise<void> => {
    try {
      const defaultData = {
        heroSection: {
          title: 'Biz Kimiz?',
          description: 'HatTravel olarak, dünyanın dört bir yanındaki unutulmaz deneyimleri siz değerli misafirlerimize sunuyoruz.'
        },
        statistics: {
          yearsExperience: 12,
          happyCustomers: 15000,
          toursCompleted: 820,
          destinations: 65
        },
        missionVision: {
          mission: 'Müşterilerimize benzersiz seyahat deneyimleri sunmak, kültürler arası köprüler kurmak ve sürdürülebilir turizmi desteklemek.',
          vision: 'Dünyanın önde gelen seyahat markalarından biri olmak ve her yolculuğu hayat boyu hatırlanacak anılara dönüştürmek.',
          values: ['Güven', 'Kalite', 'Tutku', 'Sürdürülebilirlik']
        },
        teamMembers: [
          {
            id: '1',
            name: 'Ahmet Yılmaz',
            position: 'Genel Müdür',
            bio: '15 yıllık turizm deneyimi ile HatTravel\'in kurucusu ve genel müdürü. Müşteri memnuniyetini her şeyin üstünde tutuyor.',
            image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face',
            email: 'ahmet@hattravel.com',
            phone: '+90 555 123 45 67',
            socialMedia: {
              linkedin: 'https://linkedin.com/in/ahmetyilmaz',
              twitter: 'https://twitter.com/ahmetyilmaz'
            },
            department: 'Yönetim',
            isActive: true,
            order: 1
          },
          {
            id: '2',
            name: 'Fatma Demir',
            position: 'Operasyon Müdürü',
            bio: 'Tur operasyonları ve müşteri hizmetleri konusunda uzman. 10 yıldır seyahat sektöründe faaliyet gösteriyor.',
            image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face',
            email: 'fatma@hattravel.com',
            phone: '+90 555 234 56 78',
            socialMedia: {
              linkedin: 'https://linkedin.com/in/fatmademir',
              instagram: 'https://instagram.com/fatmademir'
            },
            department: 'Operasyon',
            isActive: true,
            order: 2
          },
          {
            id: '3',
            name: 'Mehmet Özkan',
            position: 'Rehber',
            bio: 'Anadolu\'nun her karışını bilen deneyimli rehberimiz. Tarihi ve kültürel bilgileri ile turları unutulmaz kılıyor.',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
            email: 'mehmet@hattravel.com',
            phone: '+90 555 345 67 89',
            socialMedia: {
              instagram: 'https://instagram.com/mehmetozkan',
              facebook: 'https://facebook.com/mehmetozkan'
            },
            department: 'Rehberlik',
            isActive: true,
            order: 3
          },
          {
            id: '4',
            name: 'Zeynep Kaya',
            position: 'Pazarlama Müdürü',
            bio: 'Dijital pazarlama ve sosyal medya konusunda uzman. HatTravel\'in online varlığını güçlendiriyor.',
            image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face',
            email: 'zeynep@hattravel.com',
            phone: '+90 555 456 78 90',
            socialMedia: {
              linkedin: 'https://linkedin.com/in/zeynepkaya',
              twitter: 'https://twitter.com/zeynepkaya',
              instagram: 'https://instagram.com/zeynepkaya'
            },
            department: 'Pazarlama',
            isActive: true,
            order: 4
          },
          {
            id: '5',
            name: 'Can Aydın',
            position: 'Satış Temsilcisi',
            bio: 'Müşteri danışmanlığı ve satış konusunda uzman. Her müşteriye özel çözümler sunuyor.',
            image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&h=300&fit=crop&crop=face',
            email: 'can@hattravel.com',
            phone: '+90 555 567 89 01',
            socialMedia: {
              linkedin: 'https://linkedin.com/in/canaydin'
            },
            department: 'Satış',
            isActive: true,
            order: 5
          },
          {
            id: '6',
            name: 'Ayşe Yurt',
            position: 'Muhasebe Uzmanı',
            bio: 'Finansal işlemler ve muhasebe konusunda deneyimli. Şirketin mali yapısını yönetiyor.',
            image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=300&fit=crop&crop=face',
            email: 'ayse@hattravel.com',
            phone: '+90 555 678 90 12',
            socialMedia: {
              linkedin: 'https://linkedin.com/in/ayseyurt'
            },
            department: 'Mali İşler',
            isActive: true,
            order: 6
          }
        ],
        companyHistory: {
          milestones: [
            {
              id: '1',
              year: 2012,
              title: 'HatTravel\'in Kuruluşu',
              description: 'İstanbul\'da küçük bir ofisle turizm sektöründeki yolculuğumuza başladık.',
              isActive: true,
              order: 1
            },
            {
              id: '2',
              year: 2015,
              title: 'İlk Büyük Tur',
              description: 'Kapadokya ve Pamukkale turlarımızla büyük beğeni topladık ve müşteri sayımız hızla arttı.',
              isActive: true,
              order: 2
            },
            {
              id: '3',
              year: 2018,
              title: 'Uluslararası Expansion',
              description: 'Balkanlar ve Avrupa turlarına başlayarak uluslararası arenaya adım attık.',
              isActive: true,
              order: 3
            },
            {
              id: '4',
              year: 2020,
              title: 'Digital Transformation',
              description: 'Pandemi sürecinde dijital platformlara yatırım yaparak online rezervasyon sistemimizi geliştirdik.',
              isActive: true,
              order: 4
            },
            {
              id: '5',
              year: 2022,
              title: '10.000 Mutlu Müşteri',
              description: 'Müşteri memnuniyetimizi %98\'e çıkararak 10.000\'inci mutlu müşterimizi ağırladık.',
              isActive: true,
              order: 5
            },
            {
              id: '6',
              year: 2024,
              title: 'Sürdürülebilir Turizm',
              description: 'Çevre dostu turlarımızla sürdürülebilir turizm konusunda öncü olmaya devam ediyoruz.',
              isActive: true,
              order: 6
            }
          ]
        },
        certificates: [
          {
            id: '1',
            name: 'TÜRSAB A Grubu Seyahat Acentesi',
            issuer: 'TÜRSAB',
            year: 2012,
            image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&h=300&fit=crop',
            description: 'Türkiye Seyahat Acentaları Birliği A Grubu üyelik belgesi',
            isActive: true,
            order: 1
          },
          {
            id: '2',
            name: 'ISO 9001 Kalite Yönetim Sistemi',
            issuer: 'TSE',
            year: 2018,
            image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=300&fit=crop',
            description: 'Kalite yönetim sistemi standardı sertifikası',
            isActive: true,
            order: 2
          },
          {
            id: '3',
            name: 'Tursab Güvence Sistemi',
            issuer: 'TÜRSAB',
            year: 2020,
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
            description: 'Müşteri güvencesi ve finansal güvence sistemi sertifikası',
            isActive: true,
            order: 3
          },
          {
            id: '4',
            name: 'Sürdürülebilir Turizm Sertifikası',
            issuer: 'Turizm Bakanlığı',
            year: 2023,
            image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop',
            description: 'Çevre dostu ve sürdürülebilir turizm uygulamaları sertifikası',
            isActive: true,
            order: 4
          }
        ],
        sections: {
          statistics: { isActive: true, title: 'İstatistiklerimiz' },
          missionVision: { isActive: true, title: 'Misyon & Vizyon' },
          values: { isActive: true, title: 'Değerlerimiz' },
          team: { isActive: true, title: 'Ekibimiz' },
          history: { isActive: true, title: 'Tarihçemiz' },
          certificates: { isActive: true, title: 'Sertifikalarımız' }
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'aboutPage', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan hakkımızda verileri oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Contact Page Services
export const contactPageService = {
  // Get contact page data
  getContactPageData: async (): Promise<any> => {
    try {
      const contactDoc = await getDoc(doc(db, 'contactPage', 'main'));
      if (contactDoc.exists()) {
        return { id: contactDoc.id, ...contactDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update contact page data
  updateContactPageData: async (contactData: any): Promise<void> => {
    try {
      const docRef = doc(db, 'contactPage', 'main');
      await setDoc(docRef, {
        ...contactData,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error: any) {
      throw new Error('İletişim sayfası güncellenirken hata oluştu: ' + error.message);
    }
  },

  // Initialize default contact page data
  initializeContactPageData: async (): Promise<void> => {
    try {
      const defaultData = {
        heroSection: {
          title: 'İletişim',
          description: 'Sorularınız için bizimle iletişime geçin. Size en kısa sürede dönüş yapacağız.'
        },
        contactInfo: {
          phones: ['+90 212 555 00 00', '+90 532 000 00 00'],
          emails: ['info@hattravel.com', 'destek@hattravel.com'],
          address: {
            street: 'İstiklal Cd 100',
            district: 'Beyoğlu',
            city: 'İstanbul',
            country: 'Türkiye'
          },
          officeHours: {
            monday: '09:00-18:00',
            tuesday: '09:00-18:00',
            wednesday: '09:00-18:00',
            thursday: '09:00-18:00',
            friday: '09:00-18:00',
            saturday: '10:00-16:00',
            sunday: 'Kapalı'
          },
          mapLocation: {
            latitude: 41.0369,
            longitude: 28.9862,
            googleMapsApiKey: ''
          }
        },
        socialMedia: {
          whatsapp: '',
          facebook: '',
          instagram: '',
          twitter: '',
          linkedin: '',
          youtube: ''
        },
        faq: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'contactPage', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan iletişim verileri oluşturulurken hata oluştu: ' + error.message);
    }
  },

  // Submit contact form
  submitContactForm: async (formData: any): Promise<void> => {
    try {
      await addDoc(collection(db, 'contactMessages'), {
        ...formData,
        isRead: false,
        createdAt: serverTimestamp()
      });
    } catch (error: any) {
      throw new Error('Mesaj gönderilirken hata oluştu: ' + error.message);
    }
  },

  // Get contact messages (for admin)
  getContactMessages: async (): Promise<any[]> => {
    try {
      const q = query(
        collection(db, 'contactMessages'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      throw error;
    }
  },

  // Mark message as read
  markMessageAsRead: async (messageId: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'contactMessages', messageId), { 
        isRead: true,
        readAt: serverTimestamp()
      });
    } catch (error: any) {
      throw new Error('Mesaj okundu olarak işaretlenirken hata oluştu: ' + error.message);
    }
  },

  // Delete contact message
  deleteContactMessage: async (messageId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'contactMessages', messageId));
    } catch (error: any) {
      throw new Error('Mesaj silinirken hata oluştu: ' + error.message);
    }
  }
};

// FAQ Page Services
export const faqPageService = {
  // Get FAQ page data
  getFAQPageData: async (): Promise<any> => {
    try {
      const faqDoc = await getDoc(doc(db, 'faqPage', 'main'));
      if (faqDoc.exists()) {
        return { id: faqDoc.id, ...faqDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update FAQ page data
  updateFAQPageData: async (faqData: any): Promise<void> => {
    try {
      const docRef = doc(db, 'faqPage', 'main');
      await setDoc(docRef, {
        ...faqData,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error: any) {
      throw new Error('SSS sayfası güncellenirken hata oluştu: ' + error.message);
    }
  },

  // Initialize default FAQ page data
  initializeFAQPageData: async (): Promise<void> => {
    try {
      const defaultData = {
        heroSection: {
          title: 'Sık Sorulan Sorular',
          description: 'Merak ettiğiniz konular hakkında en güncel bilgilere buradan ulaşabilirsiniz. Aradığınızı bulamazsanız, bizimle iletişime geçmekten çekinmeyin.'
        },
        faq: [
          {
            id: '1',
            question: 'Tur rezervasyonumu nasıl yapabilirim?',
            answer: 'Web sitemiz üzerinden istediğiniz turu seçip formu doldurarak rezervasyon yapabilirsiniz. Rezervasyon işlemi tamamlandıktan sonra size onay e-postası gönderilecektir.',
            isActive: true,
            order: 1
          },
          {
            id: '2',
            question: 'Ödeme yöntemleriniz nelerdir?',
            answer: 'Kredi kartı, banka havalesi ve kapıda ödeme seçeneklerimiz mevcuttur. Güvenli ödeme altyapımız sayesinde tüm işlemleriniz korunmaktadır.',
            isActive: true,
            order: 2
          },
          {
            id: '3',
            question: 'Rezervasyonumu iptal edebilir miyim?',
            answer: 'Evet, belirli koşullar altında rezervasyonunuzu iptal edebilirsiniz. İptal koşulları ve ücret politikamız hakkında detaylı bilgi için müşteri hizmetlerimizle iletişime geçebilirsiniz.',
            isActive: true,
            order: 3
          }
        ],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'faqPage', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan SSS verileri oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Booking Terms Services
export const bookingTermsService = {
  // Get booking terms data
  getBookingTermsData: async (): Promise<any> => {
    try {
      const termsDoc = await getDoc(doc(db, 'bookingTerms', 'main'));
      if (termsDoc.exists()) {
        return { id: termsDoc.id, ...termsDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update booking terms data
  updateBookingTermsData: async (termsData: any): Promise<void> => {
    try {
      const docRef = doc(db, 'bookingTerms', 'main');
      await setDoc(docRef, {
        ...termsData,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error: any) {
      throw new Error('Rezervasyon koşulları güncellenirken hata oluştu: ' + error.message);
    }
  },

  // Initialize default booking terms data
  initializeBookingTermsData: async (): Promise<void> => {
    try {
      const defaultData = {
        title: 'Rezervasyon Koşulları',
        description: 'Rezervasyon yapmadan önce aşağıdaki koşulları ve şartları dikkatlice okuyunuz.',
        content: `## Genel Koşullar

Bu rezervasyon koşulları, HatTravel şirketi ile müşteriler arasındaki tur rezervasyonları için geçerlidir.

## Rezervasyon İşlemleri

### 1. Rezervasyon Onayı
- Rezervasyonunuz, ödeme işleminizin tamamlanmasından sonra kesinleşir
- Rezervasyon onayı e-posta ile gönderilir
- Rezervasyon numaranızı mutlaka saklayınız

### 2. Ödeme Koşulları
- Rezervasyon sırasında %50 kapora ödemesi gereklidir
- Kalan tutar, tur başlangıcından 7 gün önce ödenmelidir
- Kabul edilen ödeme yöntemleri: Kredi kartı, banka havalesi

### 3. İptal Koşulları
- Tur tarihinden 30 gün öncesine kadar: %10 kesinti ile iade
- Tur tarihinden 15-29 gün öncesine kadar: %25 kesinti ile iade
- Tur tarihinden 7-14 gün öncesine kadar: %50 kesinti ile iade
- Tur tarihinden 7 gün öncesinden sonra: İade yapılmaz

## Sorumluluklar

### Müşteri Sorumluluğu
- Geçerli kimlik belgesi bulundurma
- Tur saatinde hazır bulunma
- Tur kurallarına uyma

### Şirket Sorumluluğu
- Belirtilen hizmetleri sunma
- Güvenlik önlemlerini alma
- Acil durumlarda yardım sağlama

## Değişiklik ve İptal Hakları

HatTravel, hava koşulları, güvenlik durumu veya katılımcı sayısının yetersiz olması durumunda turu iptal etme hakkını saklı tutar. Bu durumda ödenen tutar tam olarak iade edilir.

## İletişim

Sorularınız için:
- E-posta: info@hattravel.com
- Telefon: +90 XXX XXX XX XX`,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      await setDoc(doc(db, 'bookingTerms', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan rezervasyon koşulları oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Privacy Policy Services
export const privacyPolicyService = {
  // Get privacy policy data
  getPrivacyPolicyData: async (): Promise<any> => {
    try {
      const policyDoc = await getDoc(doc(db, 'privacyPolicy', 'main'));
      if (policyDoc.exists()) {
        return { id: policyDoc.id, ...policyDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update privacy policy data
  updatePrivacyPolicyData: async (policyData: any): Promise<void> => {
    try {
      const docRef = doc(db, 'privacyPolicy', 'main');
      await setDoc(docRef, {
        ...policyData,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error: any) {
      throw new Error('Gizlilik politikası güncellenirken hata oluştu: ' + error.message);
    }
  },

  // Initialize default privacy policy data
  initializePrivacyPolicyData: async (): Promise<void> => {
    try {
      const defaultData = {
        title: 'Gizlilik Politikası',
        description: 'Kişisel verilerinizin güvenliği bizim için çok önemlidir. Bu politika, verilerinizin nasıl toplandığını, kullanıldığını ve korunduğunu açıklar.',
        kvkkCompliant: true,
        policySections: [
          {
            id: '1',
            title: 'Kişisel Veri Toplama',
            icon: 'DataUsage',
            items: [
              'Ad, soyad ve iletişim bilgileri',
              'Rezervasyon ve seyahat tercihleri',
              'Ödeme bilgileri (güvenli şekilde saklanır)',
              'Web sitesi kullanım verileri',
              'İletişim geçmişi ve destek kayıtları'
            ],
            order: 1,
            isActive: true
          },
          {
            id: '2',
            title: 'Veri Kullanım Amaçları',
            icon: 'Visibility',
            items: [
              'Rezervasyon işlemlerinin gerçekleştirilmesi',
              'Müşteri hizmetleri ve destek sağlanması',
              'Hizmet kalitesinin iyileştirilmesi',
              'Yasal yükümlülüklerin yerine getirilmesi',
              'Güvenlik ve dolandırıcılık önleme'
            ],
            order: 2,
            isActive: true
          },
          {
            id: '3',
            title: 'Veri Güvenliği',
            icon: 'Lock',
            items: [
              'SSL şifreleme ile güvenli veri transferi',
              'Düzenli güvenlik güncellemeleri',
              'Erişim kontrolü ve yetkilendirme',
              'Veri yedekleme ve felaket kurtarma',
              'Personel güvenlik eğitimleri'
            ],
            order: 3,
            isActive: true
          },
          {
            id: '4',
            title: 'Veri Paylaşımı',
            icon: 'Share',
            items: [
              'Sadece gerekli durumlarda üçüncü taraflarla paylaşım',
              'Yasal zorunluluklar dışında izinsiz paylaşım yoktur',
              'Paylaşım öncesi güvenlik değerlendirmesi',
              'Veri paylaşım anlaşmaları',
              'Şeffaf paylaşım politikası'
            ],
            order: 4,
            isActive: true
          },
          {
            id: '5',
            title: 'Veri Saklama ve Silme',
            icon: 'Delete',
            items: [
              'Yasal zorunluluklar kadar veri saklama',
              'Talep üzerine veri silme hakkı',
              'Otomatik veri temizleme süreçleri',
              'Veri arşivleme politikası',
              'Silme işlemi onay süreci'
            ],
            order: 5,
            isActive: true
          }
        ],
        importantNotes: [
          {
            id: '1',
            type: 'info',
            title: 'KVKK Uyumluluğu',
            content: 'Bu politika 6698 sayılı Kişisel Verilerin Korunması Kanunu uyarınca hazırlanmıştır.',
            order: 1,
            isActive: true
          },
          {
            id: '2',
            type: 'warning',
            title: 'Güncellemeler',
            content: 'Bu politika düzenli olarak güncellenir. Değişiklikler web sitemizde yayınlanır.',
            order: 2,
            isActive: true
          },
          {
            id: '3',
            type: 'success',
            title: 'Güvenli Veri',
            content: 'Kişisel verileriniz en yüksek güvenlik standartları ile korunmaktadır.',
            order: 3,
            isActive: true
          }
        ],
        contactInfo: {
          dataController: 'info@hattravel.com',
          kvkkEmail: 'kvkk@hattravel.com'
        },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      await setDoc(doc(db, 'privacyPolicy', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan gizlilik politikası oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Cancellation Refund Services
export const cancellationRefundService = {
  // Get cancellation refund data
  getCancellationRefundData: async (): Promise<any> => {
    try {
      const refundDoc = await getDoc(doc(db, 'cancellationRefund', 'main'));
      if (refundDoc.exists()) {
        return { id: refundDoc.id, ...refundDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update cancellation refund data
  updateCancellationRefundData: async (refundData: any): Promise<void> => {
    try {
      const docRef = doc(db, 'cancellationRefund', 'main');
      await setDoc(docRef, {
        ...refundData,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error: any) {
      throw new Error('İptal ve iade politikası güncellenirken hata oluştu: ' + error.message);
    }
  },

  // Initialize default cancellation refund data
  initializeCancellationRefundData: async (): Promise<void> => {
    try {
      const defaultData = {
        title: 'İptal ve İade Şartları',
        description: 'Esnek iptal politikamız ile güvenle rezervasyon yapın. İptal ve iade koşullarımızı detaylı olarak inceleyin.',
        content: `## Genel İptal Kuralları

Bu iptal ve iade şartları, HatTravel şirketi ile yapılan tüm rezervasyonlar için geçerlidir.

## İptal Süreleri ve İade Oranları

İptal süreniz, tur başlangıç tarihine göre hesaplanır ve aşağıdaki oranlarda iade yapılır.

## İade İşlemleri

İade işlemleri, iptal talebinizin onaylanmasından sonra başlatılır ve ödeme yönteminize göre belirtilen sürelerde hesabınıza yansır.

## Özel Durumlar

Hava koşulları, doğal afetler veya sağlık sorunları gibi öngörülemeyen durumlarda özel değerlendirme yapılabilir.

## İletişim

İptal ve iade işlemleri için:
- E-posta: iptal@hattravel.com
- Telefon: +90 XXX XXX XX XX
- WhatsApp: +90 XXX XXX XX XX`,
        cancellationPolicies: [
          {
            id: '1',
            period: '72+ saat öncesi',
            refund: '%100 iade',
            conditions: 'Ücretsiz iptal ve tam iade',
            color: 'success',
            order: 1,
            isActive: true
          },
          {
            id: '2',
            period: '48-72 saat öncesi',
            refund: '%75 iade',
            conditions: 'Küçük bir işlem ücreti alınır',
            color: 'info',
            order: 2,
            isActive: true
          },
          {
            id: '3',
            period: '24-48 saat öncesi',
            refund: '%50 iade',
            conditions: 'Yarı yarıya iade yapılır',
            color: 'warning',
            order: 3,
            isActive: true
          },
          {
            id: '4',
            period: '24 saatten az',
            refund: 'İade yok',
            conditions: 'Maalesef iade yapılamaz',
            color: 'error',
            order: 4,
            isActive: true
          }
        ],
        refundMethods: [
          {
            id: '1',
            method: 'Kredi Kartı',
            time: '3-5 iş günü',
            icon: 'Payment',
            order: 1,
            isActive: true
          },
          {
            id: '2',
            method: 'Banka Havalesi',
            time: '1-3 iş günü',
            icon: 'Receipt',
            order: 2,
            isActive: true
          },
          {
            id: '3',
            method: 'Nakit',
            time: 'Anında',
            icon: 'MoneyOff',
            order: 3,
            isActive: true
          }
        ],
        importantNotes: [
          {
            id: '1',
            type: 'info',
            title: 'İptal Süreleri',
            content: 'İptal süreleri tur başlangıç saatine göre hesaplanır.',
            order: 1,
            isActive: true
          },
          {
            id: '2',
            type: 'warning',
            title: 'Özel Durumlar',
            content: 'Hava durumu, doğal afetler gibi zorlayıcı sebeplerde farklı kurallar uygulanabilir.',
            order: 2,
            isActive: true
          },
          {
            id: '3',
            type: 'success',
            title: 'Hızlı İade',
            content: 'İade işlemleri en kısa sürede tamamlanır.',
            order: 3,
            isActive: true
          }
        ],
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      await setDoc(doc(db, 'cancellationRefund', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan iptal ve iade politikası oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Help Center Services
export const helpCenterService = {
  // Get help center data
  getHelpCenterData: async (): Promise<any> => {
    try {
      const helpDoc = await getDoc(doc(db, 'helpCenter', 'main'));
      if (helpDoc.exists()) {
        return { id: helpDoc.id, ...helpDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update help center data
  updateHelpCenterData: async (helpData: any): Promise<void> => {
    try {
      const docRef = doc(db, 'helpCenter', 'main');
      await setDoc(docRef, {
        ...helpData,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error: any) {
      throw new Error('Yardım merkezi güncellenirken hata oluştu: ' + error.message);
    }
  },

  // Initialize default help center data
  initializeHelpCenterData: async (): Promise<void> => {
    try {
      const defaultData = {
        title: 'Yardım Merkezi',
        description: 'Sorularınızın cevaplarını bulun veya bizimle iletişime geçin. Size yardımcı olmaktan mutluluk duyarız.',
        searchPlaceholder: 'Sorunuzu yazın veya anahtar kelime arayın...',
        helpCategories: [
          {
            id: '1',
            title: 'Rezervasyon',
            icon: 'TrendingUp',
            color: '#667eea',
            order: 1,
            isActive: true,
            questions: [
              {
                id: '1',
                question: 'Rezervasyon nasıl yapabilirim?',
                answer: 'Web sitemizden istediğiniz tur paketini seçerek online rezervasyon yapabilir veya telefon ile bizimle iletişime geçebilirsiniz. Rezervasyon için %30 ön ödeme yeterlidir.',
                order: 1,
                isActive: true
              },
              {
                id: '2',
                question: 'Rezervasyon onayı ne kadar sürede gelir?',
                answer: 'Online rezervasyonlar anında onaylanır. Telefon ile yapılan rezervasyonlar 24 saat içinde onaylanır ve e-posta ile bilgilendirme yapılır.',
                order: 2,
                isActive: true
              },
              {
                id: '3',
                question: 'Rezervasyon değişikliği yapabilir miyim?',
                answer: 'Evet, tur başlangıcından 72 saat öncesine kadar ücretsiz değişiklik yapabilirsiniz. Tarih değişikliklerinde fiyat farkı olabilir.',
                order: 3,
                isActive: true
              }
            ]
          },
          {
            id: '2',
            title: 'Ödeme',
            icon: 'Payment',
            color: '#764ba2',
            order: 2,
            isActive: true,
            questions: [
              {
                id: '1',
                question: 'Hangi ödeme yöntemlerini kabul ediyorsunuz?',
                answer: 'Kredi kartı, banka havalesi, EFT ve nakit ödeme seçeneklerimiz mevcuttur. Online ödemelerde SSL güvenlik sertifikası kullanılmaktadır.',
                order: 1,
                isActive: true
              },
              {
                id: '2',
                question: 'Taksitli ödeme yapabilir miyim?',
                answer: 'Evet, 3-6-9 taksit seçeneklerimiz mevcuttur. Taksitli ödeme için kredi kartı kullanmanız gerekmektedir.',
                order: 2,
                isActive: true
              },
              {
                id: '3',
                question: 'Ödeme güvenli mi?',
                answer: 'Tüm ödemelerimiz SSL şifreleme ile korunmaktadır. Kredi kartı bilgileriniz asla saklanmaz.',
                order: 3,
                isActive: true
              }
            ]
          },
          {
            id: '3',
            title: 'İptal ve İade',
            icon: 'Cancel',
            color: '#f093fb',
            order: 3,
            isActive: true,
            questions: [
              {
                id: '1',
                question: 'İptal politikası nedir?',
                answer: 'Tur başlangıcından 48 saat öncesine kadar ücretsiz iptal yapabilirsiniz. 48 saatten az süre kala iptal durumunda %50 iade yapılır.',
                order: 1,
                isActive: true
              },
              {
                id: '2',
                question: 'İade ne kadar sürede yapılır?',
                answer: 'Kredi kartı iadeleri 3-5 iş günü, banka havalesi iadeleri 1-3 iş günü içinde tamamlanır.',
                order: 2,
                isActive: true
              },
              {
                id: '3',
                question: 'Hava durumu nedeniyle iptal olursa ne olur?',
                answer: 'Hava durumu nedeniyle iptal edilen turlar için tam iade yapılır veya alternatif tarih önerilir.',
                order: 3,
                isActive: true
              }
            ]
          },
          {
            id: '4',
            title: 'Tur Detayları',
            icon: 'Category',
            color: '#4facfe',
            order: 4,
            isActive: true,
            questions: [
              {
                id: '1',
                question: 'Tur rehberleri kimlerdir?',
                answer: 'Tüm rehberlerimiz lisanslı ve deneyimli profesyonellerdir. Yerel rehberler ve uzman tur rehberleri ile hizmet veriyoruz.',
                order: 1,
                isActive: true
              },
              {
                id: '2',
                question: 'Tur sırasında yemek dahil mi?',
                answer: 'Tur paketlerimizin çoğunda kahvaltı dahildir. Öğle ve akşam yemekleri tur detaylarında belirtilir.',
                order: 2,
                isActive: true
              },
              {
                id: '3',
                question: 'Tur sırasında konaklama nasıl?',
                answer: '3-4 yıldızlı otellerde konaklama sağlanır. Tek kişilik, çift kişilik ve aile odaları mevcuttur.',
                order: 3,
                isActive: true
              }
            ]
          }
        ],
        supportMethods: [
          {
            id: '1',
            title: 'Telefon Desteği',
            icon: 'Phone',
            contact: '+90 555 123 45 67',
            time: '7/24',
            color: '#667eea',
            description: 'Hızlı ve doğrudan destek',
            order: 1,
            isActive: true
          },
          {
            id: '2',
            title: 'WhatsApp',
            icon: 'WhatsApp',
            contact: '+90 555 123 45 67',
            time: '7/24',
            color: '#25D366',
            description: 'Mesaj ve dosya paylaşımı',
            order: 2,
            isActive: true
          },
          {
            id: '3',
            title: 'E-posta',
            icon: 'Email',
            contact: 'destek@hattravel.com',
            time: '24 saat içinde',
            color: '#764ba2',
            description: 'Detaylı sorular için',
            order: 3,
            isActive: true
          },
          {
            id: '4',
            title: 'Canlı Destek',
            icon: 'Chat',
            contact: 'Online',
            time: '09:00-18:00',
            color: '#4facfe',
            description: 'Anlık mesajlaşma',
            order: 4,
            isActive: true
          }
        ],
        importantNotes: [
          {
            id: '1',
            type: 'info',
            title: 'Hızlı Destek',
            content: 'En hızlı destek için WhatsApp hattımızı kullanın.',
            order: 1,
            isActive: true
          },
          {
            id: '2',
            type: 'warning',
            title: 'Acil Durumlar',
            content: 'Tur sırasında acil durumlar için 7/24 telefon hattımız açıktır.',
            order: 2,
            isActive: true
          },
          {
            id: '3',
            type: 'success',
            title: 'Uzman Destek',
            content: 'Tüm destek ekibimiz seyahat konusunda uzmandır.',
            order: 3,
            isActive: true
          }
        ],
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      await setDoc(doc(db, 'helpCenter', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan yardım merkezi oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Homepage Service
export const homePageService = {
  // Ana sayfa verilerini getir
  getHomePageData: async () => {
    try {
      const docRef = doc(db, 'homePage', 'main');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      }
      
      return null;
    } catch (error: any) {
      throw new Error('Ana sayfa verileri alınırken hata oluştu: ' + error.message);
    }
  },

  // Ana sayfa verilerini güncelle
  updateHomePageData: async (data: any) => {
    try {
      const docRef = doc(db, 'homePage', 'main');
      await updateDoc(docRef, {
        ...data,
        lastUpdated: serverTimestamp()
      });
    } catch (error: any) {
      throw new Error('Ana sayfa verileri güncellenirken hata oluştu: ' + error.message);
    }
  },

  // Varsayılan ana sayfa verilerini oluştur
  createDefaultHomePageData: async () => {
    try {
      const defaultData = {
        // Hero Section
        heroSection: {
          isActive: true,
          slides: [
            {
              id: '1',
              title: 'Keşfetmeye Hazır Mısınız?',
              subtitle: 'Hayalinizdeki tatil için doğru adrestesiniz.',
              description: 'HatTravel ile unutulmaz anılar biriktirin. Güvenli, konforlu ve keyifli seyahatler için yanınızdayız.',
              ctaText: 'Turları İncele',
              ctaLink: '/tours',
              backgroundImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2074&q=80',
              order: 1,
              isActive: true
            },
            {
              id: '2',
              title: 'Dünyayı Keşfedin',
              subtitle: 'Eşsiz destinasyonlar sizi bekliyor.',
              description: 'Profesyonel rehberlik ve kaliteli hizmet anlayışımızla seyahatinizi unutulmaz kılıyoruz.',
              ctaText: 'Hemen Rezervasyon Yap',
              ctaLink: '/tours',
              backgroundImage: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=2074&q=80',
              order: 2,
              isActive: true
            }
          ],
          autoSlide: true,
          slideInterval: 5000
        },

        // Features Section
        featuresSection: {
          isActive: true,
          title: 'Neden HatTravel?',
          subtitle: 'Size en iyi seyahat deneyimini sunmak için buradayız',
          features: [
            {
              id: '1',
              title: 'Güvenilir Hizmet',
              description: 'Yılların deneyimi ile güvenilir ve kaliteli hizmet',
              icon: 'Security',
              color: '#4CAF50',
              order: 1,
              isActive: true
            },
            {
              id: '2',
              title: 'Uzman Rehberlik',
              description: 'Profesyonel ve deneyimli rehber kadromuz',
              icon: 'Person',
              color: '#2196F3',
              order: 2,
              isActive: true
            },
            {
              id: '3',
              title: 'Uygun Fiyatlar',
              description: 'En uygun fiyatlarla kaliteli tatil imkanı',
              icon: 'AttachMoney',
              color: '#FF9800',
              order: 3,
              isActive: true
            },
            {
              id: '4',
              title: '7/24 Destek',
              description: 'Her zaman yanınızda olan müşteri destek hizmeti',
              icon: 'Support',
              color: '#9C27B0',
              order: 4,
              isActive: true
            }
          ]
        },

        // Statistics Section
        statisticsSection: {
          isActive: true,
          title: 'Rakamlarla HatTravel',
          subtitle: 'Güvenilirliğimizi rakamlar konuşuyor',
          stats: [
            {
              id: '1',
              title: 'Mutlu Müşteri',
              value: '10000+',
              icon: 'People',
              color: '#4CAF50',
              order: 1,
              isActive: true
            },
            {
              id: '2',
              title: 'Tur Paketi',
              value: '500+',
              icon: 'Tour',
              color: '#2196F3',
              order: 2,
              isActive: true
            },
            {
              id: '3',
              title: 'Destinasyon',
              value: '50+',
              icon: 'LocationOn',
              color: '#FF9800',
              order: 3,
              isActive: true
            },
            {
              id: '4',
              title: 'Yıllık Deneyim',
              value: '15+',
              icon: 'Star',
              color: '#9C27B0',
              order: 4,
              isActive: true
            }
          ]
        },

        // Popular Tours Section
        popularToursSection: {
          isActive: true,
          title: 'Popüler Turlar',
          subtitle: 'En çok tercih edilen tur paketlerimiz',
          showCount: 6,
          showFeaturedOnly: true
        },

        // Testimonials Section
        testimonialsSection: {
          isActive: true,
          title: 'Müşteri Yorumları',
          subtitle: 'Bizimle seyahat eden müşterilerimizin deneyimleri',
          testimonials: [
            {
              id: '1',
              name: 'Ahmet Yılmaz',
              location: 'İstanbul',
              rating: 5,
              comment: 'HatTravel ile gittiğimiz Kapadokya turu harikaydı. Rehberimiz çok bilgiliydi ve her şey mükemmel organize edilmişti.',
              avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
              tourName: 'Kapadokya Turu',
              date: '2024-01-15',
              order: 1,
              isActive: true
            },
            {
              id: '2',
              name: 'Fatma Demir',
              location: 'Ankara',
              rating: 5,
              comment: 'Antalya tatilimiz muhteşemdi. Otel konumu, yemekler ve aktiviteler hepsi harikaydı. Kesinlikle tekrar tercih edeceğim.',
              avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
              tourName: 'Antalya Tatili',
              date: '2024-02-20',
              order: 2,
              isActive: true
            },
            {
              id: '3',
              name: 'Mehmet Özkan',
              location: 'İzmir',
              rating: 4,
              comment: 'Pamukkale gezisi çok güzeldi. Doğal güzellikler ve termal suları harika. Fotoğraf çekmek için ideal yerler.',
              avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
              tourName: 'Pamukkale Turu',
              date: '2024-03-10',
              order: 3,
              isActive: true
            }
          ]
        },

        // Blog Section
        blogSection: {
          isActive: true,
          title: 'Seyahat Rehberi',
          subtitle: 'Seyahat ipuçları ve destinasyon rehberleri',
          showCount: 6,
          showFeaturedOnly: false
        },

        // CTA Section
        ctaSection: {
          isActive: true,
          title: 'Hayalinizdeki Tatili Planlayın',
          subtitle: 'Unutulmaz anılar biriktirmek için hemen rezervasyon yapın',
          ctaText: 'Hemen Rezervasyon Yap',
          ctaLink: '/tours',
          backgroundImage: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=2074&q=80',
          overlayOpacity: 0.6
        },

        // Contact Section
        contactSection: {
          isActive: true,
          title: 'Bizimle İletişime Geçin',
          subtitle: 'Sorularınız için 7/24 hizmetinizdeyiz',
          showContactForm: true,
          showContactInfo: true,
          showMap: false
        },

        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      await setDoc(doc(db, 'homePage', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan ana sayfa oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Footer Service
export const footerService = {
  getFooterData: async (): Promise<any> => {
    try {
      const footerDoc = await getDoc(doc(db, 'footer', 'main'));
      if (footerDoc.exists()) {
        return { id: footerDoc.id, ...footerDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting footer data:', error);
      throw error;
    }
  },

  updateFooterData: async (data: any): Promise<void> => {
    try {
      await setDoc(doc(db, 'footer', 'main'), {
        ...data,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating footer data:', error);
      throw error;
    }
  },

  initializeFooterData: async (): Promise<void> => {
    try {
      const defaultData = {
        companyInfo: {
          name: 'HatTravel',
          description: 'Türkiye\'nin önde gelen seyahat acentesi olarak, unutulmaz deneyimler yaşatmak için çalışıyoruz. Güvenilir, kaliteli ve uygun fiyatlı tur paketlerimizle hayalinizdeki tatili gerçekleştirin.'
        },
        contactInfo: {
          phones: ['+90 555 123 45 67'],
          emails: ['info@hattravel.com'],
          address: 'Hatay, Türkiye',
          whatsapp: '+90 555 123 45 67'
        },
        socialMedia: {
          facebook: 'https://facebook.com/hattravel',
          instagram: 'https://instagram.com/hattravel',
          twitter: 'https://twitter.com/hattravel',
          youtube: 'https://youtube.com/hattravel',
          whatsapp: 'https://wa.me/905551234567'
        },
        quickLinks: [
          { id: '1', text: 'Ana Sayfa', path: '/', order: 1, isActive: true },
          { id: '2', text: 'Tur Paketleri', path: '/tours', order: 2, isActive: true },
          { id: '3', text: 'Blog', path: '/blog', order: 3, isActive: true },
          { id: '4', text: 'Hakkımızda', path: '/about', order: 4, isActive: true },
          { id: '5', text: 'İletişim', path: '/contact', order: 5, isActive: true }
        ],
        supportLinks: [
          { id: '1', text: 'Sık Sorulan Sorular', path: '/faq', order: 1, isActive: true },
          { id: '2', text: 'Rezervasyon Koşulları', path: '/booking-terms', order: 2, isActive: true },
          { id: '3', text: 'Gizlilik Politikası', path: '/privacy-policy', order: 3, isActive: true },
          { id: '4', text: 'İptal ve İade', path: '/cancellation-refund', order: 4, isActive: true },
          { id: '5', text: 'Yardım Merkezi', path: '/help-center', order: 5, isActive: true }
        ],
        bottomLinks: [
          { id: '1', text: 'Rezervasyon Koşulları', path: '/booking-terms', order: 1, isActive: true },
          { id: '2', text: 'Gizlilik Politikası', path: '/privacy-policy', order: 2, isActive: true },
          { id: '3', text: 'İptal ve İade', path: '/cancellation-refund', order: 3, isActive: true }
        ],
        copyright: `© ${new Date().getFullYear()} HatTravel. Tüm hakları saklıdır.`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'footer', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan footer verileri oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Custom Tour Request Service
export const customTourRequestService = {
  // Submit custom tour request
  submitCustomTourRequest: async (requestData: any): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'customTourRequests'), {
        ...requestData,
        status: 'pending',
        isRead: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error submitting custom tour request:', error);
      throw error;
    }
  },

  // Get all custom tour requests
  getCustomTourRequests: async (): Promise<any[]> => {
    try {
      const querySnapshot = await getDocs(
        query(
          collection(db, 'customTourRequests'),
          orderBy('createdAt', 'desc')
        )
      );
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting custom tour requests:', error);
      throw error;
    }
  },

  // Get custom tour request by ID
  getCustomTourRequest: async (id: string): Promise<any | null> => {
    try {
      const docRef = doc(db, 'customTourRequests', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting custom tour request:', error);
      throw error;
    }
  },

  // Update custom tour request
  updateCustomTourRequest: async (id: string, data: any): Promise<void> => {
    try {
      await updateDoc(doc(db, 'customTourRequests', id), {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating custom tour request:', error);
      throw error;
    }
  },

  // Mark as read
  markAsRead: async (id: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'customTourRequests', id), {
        isRead: true,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking request as read:', error);
      throw error;
    }
  },

  // Delete custom tour request
  deleteCustomTourRequest: async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'customTourRequests', id));
    } catch (error) {
      console.error('Error deleting custom tour request:', error);
      throw error;
    }
  },

  // Get requests count
  getRequestsCount: async (): Promise<number> => {
    try {
      const snapshot = await getCountFromServer(collection(db, 'customTourRequests'));
      return snapshot.data().count;
    } catch (error) {
      console.error('Error getting requests count:', error);
      return 0;
    }
  },

  // Get page data
  getCustomTourRequestPageData: async (): Promise<any> => {
    try {
      const docRef = doc(db, 'customTourRequestPage', 'main');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting custom tour request page data:', error);
      throw error;
    }
  },

  // Update page data
  updateCustomTourRequestPageData: async (data: any): Promise<void> => {
    try {
      await setDoc(doc(db, 'customTourRequestPage', 'main'), {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating custom tour request page data:', error);
      throw error;
    }
  },

  // Initialize page data
  initializeCustomTourRequestPageData: async (): Promise<void> => {
    try {
      const defaultData = {
        heroSection: {
          title: 'Özel Tur Talebi',
          subtitle: 'Hayalinizdeki özel turu birlikte tasarlayalım',
          description: 'Balayı, düğün, kurumsal etkinlik veya grup turları için özel çözümler sunuyoruz. İhtiyaçlarınıza göre kişiselleştirilmiş tur paketleri hazırlıyoruz.',
          backgroundImage: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=2074&q=80'
        },
        formSection: {
          title: 'Talebinizi Bildirin',
          description: 'Aşağıdaki formu doldurarak özel tur talebinizi bize iletebilirsiniz. Uzman ekibimiz sizinle iletişime geçerek en uygun çözümü sunacaktır.',
          tourTypes: [
            {
              id: '1',
              name: 'Balayı Turu',
              description: 'Romantik ve özel balayı turları',
              icon: 'Favorite',
              order: 1,
              isActive: true
            },
            {
              id: '2',
              name: 'Düğün Turu',
              description: 'Evlilik öncesi veya sonrası özel turlar',
              icon: 'CardGiftcard',
              order: 2,
              isActive: true
            },
            {
              id: '3',
              name: 'Aile Turu',
              description: 'Aile ve çocuklarla özel turlar',
              icon: 'Family',
              order: 3,
              isActive: true
            },
            {
              id: '4',
              name: 'Grup Turu',
              description: 'Arkadaş grupları için özel turlar',
              icon: 'Group',
              order: 4,
              isActive: true
            },
            {
              id: '5',
              name: 'Kurumsal Tur',
              description: 'Şirket etkinlikleri ve kurumsal turlar',
              icon: 'Business',
              order: 5,
              isActive: true
            },
            {
              id: '6',
              name: 'Balkan Turu',
              description: 'Balkan ülkeleri gezisi turları',
              icon: 'Public',
              order: 6,
              isActive: true
            },
            {
              id: '7',
              name: 'Özel Kültür Turu',
              description: 'Kültürel ve tarihî yerler gezisi',
              icon: 'Museum',
              order: 7,
              isActive: true
            },
            {
              id: '8',
              name: 'Diğer',
              description: 'Diğer özel tur talepleri',
              icon: 'MoreHoriz',
              order: 8,
              isActive: true
            }
          ]
        },
        processSection: {
          title: 'Süreç Nasıl İşliyor?',
          description: 'Özel tur talebinizden rezervasyona kadar olan süreç 4 adımda tamamlanır.',
          steps: [
            {
              id: '1',
              title: 'Talep Gönderin',
              description: 'Formu doldurun ve talebinizi bize iletin',
              icon: 'Send',
              order: 1,
              isActive: true
            },
            {
              id: '2',
              title: 'İletişim Kuralım',
              description: '24 saat içinde sizinle iletişime geçiyoruz',
              icon: 'Phone',
              order: 2,
              isActive: true
            },
            {
              id: '3',
              title: 'Teklif Hazırlayalım',
              description: 'İhtiyaçlarınıza uygun özel teklif hazırlıyoruz',
              icon: 'Description',
              order: 3,
              isActive: true
            },
            {
              id: '4',
              title: 'Rezervasyon Yapın',
              description: 'Teklifimizi beğenirseniz rezervasyonu tamamlayın',
              icon: 'CheckCircle',
              order: 4,
              isActive: true
            }
          ]
        },
        whyChooseSection: {
          title: 'Neden Bizi Tercih Etmelisiniz?',
          description: 'Özel tur konusunda uzman ekibimiz ve avantajlarımız',
          features: [
            {
              id: '1',
              title: 'Kişiselleştirilmiş Hizmet',
              description: 'Her talebi özel olarak değerlendirip sizin için en uygun çözümü sunuyoruz',
              icon: 'PersonalVideo',
              order: 1,
              isActive: true
            },
            {
              id: '2',
              title: '15 Yıllık Deneyim',
              description: 'Seyahat sektöründeki uzun yıllara dayanan deneyimimiz',
              icon: 'Star',
              order: 2,
              isActive: true
            },
            {
              id: '3',
              title: 'Uygun Fiyat',
              description: 'Kaliteli hizmet için en uygun fiyat garantisi',
              icon: 'AttachMoney',
              order: 3,
              isActive: true
            },
            {
              id: '4',
              title: '7/24 Destek',
              description: 'Her zaman yanınızda olan profesyonel destek ekibi',
              icon: 'SupportAgent',
              order: 4,
              isActive: true
            }
          ]
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'customTourRequestPage', 'main'), defaultData);
    } catch (error: any) {
      throw new Error('Varsayılan özel tur talebi sayfa verileri oluşturulurken hata oluştu: ' + error.message);
    }
  }
};

// Export SEO Service
export { seoService } from './seoService'; 